'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import RichText from '@/components/RichText';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/contexts/LocaleContext';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';

import styles from './Modal.module.scss';

type Props = {
  children?: React.ReactNode;
  className?: string;
  clickHandler: () => void;
  modalOpen?: boolean;
  content: Record<string, any>;
  title?: string | React.ReactNode;
};

export const Modal: React.FC<Props> = ({
  children,
  className,
  clickHandler,
  content,
  modalOpen,
  title,
}) => {
  const locale = useLocale();
  const t = getDictionary(locale);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      clickHandler();
    }
  };

  if (!mounted || typeof window === 'undefined') return null;

  return createPortal(
    <div
      className={cn(
        styles.background,
        className,
        modalOpen ? styles.open : styles.close
      )}
      onClick={handleClick}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          {typeof title === 'string' ? (
            <div className={styles.title}>{title}</div>
          ) : (
            title
          )}
          <Button
            className={cn(
              'button button--level-3 button--medium button--icon',
              styles.closeButton
            )}
            onClick={clickHandler}
            aria-label={t('close')}
          >
            <svg
              className={styles.icon}
              width='16'
              height='17'
              viewBox='0 0 16 17'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path d='M12.8538 12.6702C12.9002 12.7167 12.9371 12.7718 12.9622 12.8325C12.9874 12.8932 13.0003 12.9583 13.0003 13.024C13.0003 13.0897 12.9874 13.1547 12.9622 13.2154C12.9371 13.2761 12.9002 13.3313 12.8538 13.3777C12.8073 13.4242 12.7522 13.461 12.6915 13.4862C12.6308 13.5113 12.5657 13.5242 12.5 13.5242C12.4343 13.5242 12.3693 13.5113 12.3086 13.4862C12.2479 13.461 12.1927 13.4242 12.1463 13.3777L8.00003 9.23083L3.85378 13.3777C3.75996 13.4715 3.63272 13.5242 3.50003 13.5242C3.36735 13.5242 3.2401 13.4715 3.14628 13.3777C3.05246 13.2839 2.99976 13.1566 2.99976 13.024C2.99976 12.8913 3.05246 12.764 3.14628 12.6702L7.29316 8.52396L3.14628 4.37771C3.05246 4.28389 2.99976 4.15664 2.99976 4.02396C2.99976 3.89128 3.05246 3.76403 3.14628 3.67021C3.2401 3.57639 3.36735 3.52368 3.50003 3.52368C3.63272 3.52368 3.75996 3.57639 3.85378 3.67021L8.00003 7.81708L12.1463 3.67021C12.2401 3.57639 12.3674 3.52368 12.5 3.52368C12.6327 3.52368 12.76 3.57639 12.8538 3.67021C12.9476 3.76403 13.0003 3.89128 13.0003 4.02396C13.0003 4.15664 12.9476 4.28389 12.8538 4.37771L8.70691 8.52396L12.8538 12.6702Z' />
            </svg>
          </Button>
        </div>
        {content && (
          <RichText
            content={content}
            className={styles.content}
            enableGutter={false}
          />
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};
